import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Form, Button, Table, Badge } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaArrowLeft, FaDownload, FaChartBar, FaUsers, FaFilter, FaSync } from "react-icons/fa";
import { reporteApi } from '../../services/api/reporteApi';
import "react-toastify/dist/ReactToastify.css";

const ReporteComensalesDietas = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [datosReporte, setDatosReporte] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [filtros, setFiltros] = useState({
    cliente_id: "",
    activo: ""
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  // Cargar clientes al inicializar
  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      console.log('🔍 Cargando clientes para filtros...');
      const response = await reporteApi.obtenerClientesComensalesDietas();
      
      if (response.data.success) {
        setClientes(response.data.data);
        console.log('✅ Clientes cargados:', response.data.data.length);
      } else {
        toast.error("Error al cargar la lista de clientes");
      }
    } catch (error) {
      console.error("❌ Error cargando clientes:", error);
      toast.error("Error al cargar la lista de clientes");
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value === "" ? "" : value === "true" ? true : value === "false" ? false : parseInt(value)
    }));
  };

  const obtenerDatosReporte = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Obteniendo datos del reporte con filtros:', filtros);
      
      // Preparar parámetros
      const params = {};
      if (filtros.cliente_id !== "") params.cliente_id = filtros.cliente_id;
      if (filtros.activo !== "") params.activo = filtros.activo;

      const response = await reporteApi.obtenerDatosComensalesDietas(params);
      
      if (response.data.success) {
        setDatosReporte(response.data.data);
        setEstadisticas(response.data.estadisticas);
        console.log('✅ Datos obtenidos:', response.data.data.length, 'registros');
        
        if (response.data.data.length === 0) {
          toast.info("No se encontraron comensales con dietas para los filtros especificados");
        } else {
          toast.success(`Se encontraron ${response.data.data.length} registros`);
        }
      } else {
        toast.error(response.data.message || "Error al obtener los datos del reporte");
        setDatosReporte([]);
        setEstadisticas(null);
      }
    } catch (error) {
      console.error("❌ Error obteniendo datos del reporte:", error);
      toast.error("Error al obtener los datos del reporte");
      setDatosReporte([]);
      setEstadisticas(null);
    } finally {
      setIsLoading(false);
    }
  };

  const descargarExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      console.log('📊 Generando Excel con filtros:', filtros);
      
      // Preparar parámetros
      const params = {};
      if (filtros.cliente_id !== "") params.cliente_id = filtros.cliente_id;
      if (filtros.activo !== "") params.activo = filtros.activo;

      const response = await reporteApi.descargarComensalesDietasExcel(params);
      
      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generar nombre del archivo
      let filename = 'reporte_comensales_dietas_' + new Date().toISOString().split('T')[0];
      if (filtros.cliente_id !== "") {
        const cliente = clientes.find(c => c.id === filtros.cliente_id);
        if (cliente) filename += '_' + cliente.nombre.replace(/\s+/g, '_').toLowerCase();
      }
      if (filtros.activo !== "") {
        filename += '_' + (filtros.activo ? 'activos' : 'inactivos');
      }
      filename += '.xlsx';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Excel descargado:', filename);
      toast.success("Excel generado y descargado exitosamente");
      
    } catch (error) {
      console.error("❌ Error generando Excel:", error);
      toast.error("Error al generar el archivo Excel");
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      cliente_id: "",
      activo: ""
    });
    setDatosReporte([]);
    setEstadisticas(null);
  };

  const handleBackToReports = () => {
    navigate("/reportes");
  };

  const getEstadoBadge = (activo) => {
    const esSi = activo === 'Sí' || activo === 'Si' || activo === true || activo === 1 || activo === '1';
    return esSi ? (
      <Badge bg="success">Sí</Badge>
    ) : (
      <Badge bg="secondary">No</Badge>
    );
  };

  return (
    <>
      {/* Estilos específicos del componente */}
      <style>
        {`
          .reporte-header {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(240, 147, 251, 0.2);
          }
          
          .reporte-card {
            border: none;
            border-radius: 15px;
            box-shadow: 0 5px 15px rgba(0,0,0,0.08);
            margin-bottom: 1.5rem;
          }
          
          .filtros-card {
            background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
          }
          
          .estadisticas-card {
            background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          }
          
          .btn-primary-custom {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            border: none;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          
          .btn-primary-custom:hover {
            background: linear-gradient(135deg, #f5576c 0%, #f093fb 100%);
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(240, 147, 251, 0.4);
          }
          
          .btn-success-custom {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          
          .btn-success-custom:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #11998e 100%);
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(17, 153, 142, 0.4);
          }
          
          .btn-secondary-custom {
            background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
            border: none;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          
          .btn-secondary-custom:hover {
            background: linear-gradient(135deg, #495057 0%, #343a40 100%);
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(108, 117, 125, 0.4);
          }
          
          .stats-item {
            text-align: center;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.8);
            border-radius: 10px;
            margin-bottom: 1rem;
          }
          
          .stats-number {
            font-size: 2rem;
            font-weight: 700;
            color: #f5576c;
            margin: 0;
          }
          
          .stats-label {
            color: #6c757d;
            font-size: 0.9rem;
            margin: 0;
          }
          
          .table-container {
            max-height: 600px;
            overflow-y: auto;
            border-radius: 10px;
            border: 1px solid #dee2e6;
          }
          
          .table-header {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
            position: sticky;
            top: 0;
            z-index: 10;
          }
        `}
      </style>

      <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        {/* Header */}
        <div className="reporte-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="mb-2">
                  <FaUsers className="me-3" />
                  Reporte de Comensales con Dietas
                </h1>
                <p className="mb-0 opacity-75">
                  Consulta y descarga información de comensales con dietas asignadas
                </p>
              </div>
              <Button 
                variant="outline-light" 
                onClick={handleBackToReports}
                className="d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Volver a Reportes
              </Button>
            </div>
          </Container>
        </div>

        <Container>
          {/* Filtros */}
          <Card className="reporte-card filtros-card">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0 d-flex align-items-center">
                <FaFilter className="me-2 text-primary" />
                Filtros de Búsqueda
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cliente</Form.Label>
                    <Form.Select
                      name="cliente_id"
                      value={filtros.cliente_id}
                      onChange={handleFiltroChange}
                    >
                      <option value="">Todos los clientes</option>
                      {clientes.map((cliente) => (
                        <option key={cliente.id} value={cliente.id}>
                          {cliente.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      name="activo"
                      value={filtros.activo}
                      onChange={handleFiltroChange}
                    >
                      <option value="">Todos los estados</option>
                      <option value="true">Activo</option>
                      <option value="false">Inactivo</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={4} className="d-flex align-items-end">
                  <div className="d-flex gap-2 w-100">
                    <Button
                      className="btn-primary-custom flex-fill"
                      onClick={obtenerDatosReporte}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <FaSync className="me-2 fa-spin" />
                          Consultando...
                        </>
                      ) : (
                        <>
                          <FaChartBar className="me-2" />
                          Consultar
                        </>
                      )}
                    </Button>
                    
                    <Button
                      className="btn-secondary-custom"
                      onClick={limpiarFiltros}
                      disabled={isLoading}
                    >
                      Limpiar
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Estadísticas */}
          {estadisticas && (
            <Card className="reporte-card estadisticas-card">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FaChartBar className="me-2 text-info" />
                  Estadísticas del Reporte
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <div className="stats-item">
                      <h3 className="stats-number">{estadisticas.total_comensales}</h3>
                      <p className="stats-label">Comensales Únicos</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="stats-item">
                      <h3 className="stats-number">{estadisticas.total_asignaciones}</h3>
                      <p className="stats-label">Total Asignaciones</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="stats-item">
                      <h3 className="stats-number text-success">{estadisticas.asignaciones_activas}</h3>
                      <p className="stats-label">Asignaciones Activas</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="stats-item">
                      <h3 className="stats-number text-secondary">{estadisticas.asignaciones_inactivas}</h3>
                      <p className="stats-label">Asignaciones Inactivas</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Resultados */}
          {datosReporte.length > 0 && (
            <Card className="reporte-card">
              <Card.Header className="bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-flex align-items-center">
                  <FaUsers className="me-2 text-success" />
                  Resultados ({datosReporte.length} registros)
                </h5>
                <Button
                  className="btn-success-custom"
                  onClick={descargarExcel}
                  disabled={isGeneratingExcel}
                >
                  {isGeneratingExcel ? (
                    <>
                      <FaSync className="me-2 fa-spin" />
                      Generando Excel...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="me-2" />
                      Descargar Excel
                    </>
                  )}
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-container">
                  <Table striped hover responsive className="mb-0">
                    <thead className="table-header">
                      <tr>
                        <th>Cliente</th>
                        <th>Núm. Documento</th>
                        <th>Nombre Completo</th>
                        <th>Tipo de Dieta</th>
                        <th>Fecha Inicio</th>
                        <th>Fecha Fin</th>
                        <th>Estado</th>
                        <th>Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datosReporte.map((item, index) => (
                        <tr key={index}>
                          <td>{item.cliente}</td>
                          <td><strong>{item.numero_documento_comedor}</strong></td>
                          <td>{item.nombre_completo}</td>
                          <td><Badge bg="info">{item.tipo_dieta}</Badge></td>
                          <td>{item.fecha_inicio ? new Date(item.fecha_inicio).toLocaleDateString() : 'N/A'}</td>
                          <td>{item.fecha_fin ? new Date(item.fecha_fin).toLocaleDateString() : 'Sin fecha fin'}</td>
                          <td>{getEstadoBadge(item.activo)}</td>
                          <td className="text-muted">{item.observaciones}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Mensaje cuando no hay datos */}
          {!isLoading && datosReporte.length === 0 && estadisticas === null && (
            <Card className="reporte-card text-center">
              <Card.Body className="py-5">
                <FaUsers size={48} className="text-muted mb-3" />
                <h5 className="text-muted">No hay datos para mostrar</h5>
                <p className="text-muted">
                  Utiliza los filtros de búsqueda y haz clic en "Consultar" para obtener los datos del reporte.
                </p>
              </Card.Body>
            </Card>
          )}
        </Container>

        <ToastContainer 
          position="top-right" 
          autoClose={5000} 
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </>
  );
};

export default ReporteComensalesDietas;
